import { useEffect, useMemo, useState } from "react";
import { buildRelativeMonthRange, parseDateLocal, toLocalYMD } from "@/utils/dateUtils";
import { useCategoryTrendBalance } from "@/domains/analytics/hooks/useAnalyticsQueries";

export type TrendCategoryOption = { id: number; name: string };

export type MonthlyCategoryTrendPoint = {
  id: string;
  label: string;
  expense: number;
  income: number;
};

const CATEGORY_TREND_MONTHS = 6;

type Params = {
  cycleEnd: string;
  initialCategoryId: number | null;
  options: TrendCategoryOption[];
};

/**
 * State and data for the "Flujo mensual por categoría" panel: the selected category, the
 * six-month trend query and the per-month points that the chart and metrics render.
 */
export const useCategoryMonthlyFlow = ({ cycleEnd, initialCategoryId, options }: Params) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    initialCategoryId ?? options[0]?.id ?? null,
  );
  const [hasUserPicked, setHasUserPicked] = useState(false);

  // initialCategoryId arrives late: it depends on the cycle breakdown query, which resolves
  // after this panel mounts. Without following it afterwards the panel would stay pinned to
  // whatever fallback happened to be ready first, ignoring the category the rest of the
  // page points at. A manual pick wins from then on.
  useEffect(() => {
    if (hasUserPicked) return;
    if (initialCategoryId == null || initialCategoryId === selectedCategoryId) return;
    setSelectedCategoryId(initialCategoryId);
  }, [hasUserPicked, initialCategoryId, selectedCategoryId]);

  useEffect(() => {
    const selectedStillExists = options.some((option) => option.id === selectedCategoryId);
    if (selectedStillExists) return;
    setSelectedCategoryId(initialCategoryId ?? options[0]?.id ?? null);
  }, [initialCategoryId, options, selectedCategoryId]);

  const selectedCategory = options.find((option) => option.id === selectedCategoryId) ?? options[0] ?? null;

  const categoryTrendRange = useMemo(() => {
    const referenceDate = parseDateLocal(cycleEnd);
    return {
      start: buildRelativeMonthRange(-(CATEGORY_TREND_MONTHS - 1), referenceDate).start,
      end: buildRelativeMonthRange(0, referenceDate).end,
    };
  }, [cycleEnd]);

  const { data: trendCategoryBalanceResponse, isLoading, isFetching } = useCategoryTrendBalance({
    categoryId: selectedCategory?.id ?? null,
    start: categoryTrendRange.start,
    end: categoryTrendRange.end,
  });

  const points = useMemo<MonthlyCategoryTrendPoint[]>(() => {
    const trendCategoryBalance = trendCategoryBalanceResponse ?? {};
    const referenceDate = parseDateLocal(cycleEnd);
    const months = Array.from({ length: CATEGORY_TREND_MONTHS }, (_, index) => {
      const monthOffset = index - (CATEGORY_TREND_MONTHS - 1);
      const monthStart = new Date(referenceDate.getFullYear(), referenceDate.getMonth() + monthOffset, 1);
      return {
        id: toLocalYMD(monthStart),
        label: monthStart.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
        expense: 0,
        income: 0,
      };
    });

    const monthMap = new Map(months.map((month) => [month.id, month]));
    const monthlyGroups = trendCategoryBalance.monthly_categories_balance;

    if (Array.isArray(monthlyGroups) && monthlyGroups.length > 0) {
      monthlyGroups.forEach((group) => {
        const monthRaw = String(group.month ?? "");
        if (!monthRaw) return;
        const parsedMonth = parseDateLocal(monthRaw);
        const monthKey = toLocalYMD(new Date(parsedMonth.getFullYear(), parsedMonth.getMonth(), 1));
        const bucket = monthMap.get(monthKey);
        if (!bucket) return;
        const categoryRow = group.categories_balance?.[0];
        bucket.expense = Number(categoryRow?.category_total_expense ?? 0);
        bucket.income = Number(categoryRow?.category_total_deposit ?? 0);
      });
    } else {
      trendCategoryBalance.categories_balance?.forEach((entry) => {
        const groupMonth = String(entry.month ?? "");
        const nestedEntry = Array.isArray(entry.categories_balance) ? entry.categories_balance[0] : entry;
        const parsedMonth = parseDateLocal(groupMonth);
        if (Number.isNaN(parsedMonth.getTime())) return;
        const monthKey = toLocalYMD(new Date(parsedMonth.getFullYear(), parsedMonth.getMonth(), 1));
        const bucket = monthMap.get(monthKey);
        if (!bucket) return;
        bucket.expense = Number(nestedEntry?.category_total_expense ?? 0);
        bucket.income = Number(nestedEntry?.category_total_deposit ?? 0);
      });
    }

    return months;
  }, [cycleEnd, trendCategoryBalanceResponse]);

  const averages = useMemo(() => {
    if (points.length === 0) return { expense: 0, income: 0 };
    const totals = points.reduce(
      (sum, point) => ({ expense: sum.expense + point.expense, income: sum.income + point.income }),
      { expense: 0, income: 0 },
    );
    return { expense: totals.expense / points.length, income: totals.income / points.length };
  }, [points]);

  return {
    selectedCategory,
    selectCategory: (id: number) => {
      setHasUserPicked(true);
      setSelectedCategoryId(id);
    },
    points,
    averages,
    isLoading,
    isFetching,
    hasResponse: trendCategoryBalanceResponse !== undefined,
  };
};
